import { useEffect } from 'react'

function getFocusableElements(container) {
  if (!container) return []
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')

  const els = Array.from(container.querySelectorAll(selector))
  // Filter out elements that are not actually visible
  return els.filter((el) => {
    const style = window.getComputedStyle(el)
    return style.visibility !== 'hidden' && style.display !== 'none'
  })
}

/**
 * Minimal, production-safe modal accessibility:
 * - Focus initial element on mount
 * - Traps Tab navigation within modal
 * - Closes on Escape (delegated to onClose)
 * - Restores focus to previously focused element on unmount
 */
export function useModalA11y({ onClose, modalRef }) {
  useEffect(() => {
    const container = modalRef?.current
    if (!container) return
    if (typeof window === 'undefined') return

    const lastActive = document.activeElement

    const focusables = getFocusableElements(container)
    const initial = focusables[0] || container
    if (typeof initial?.focus === 'function') initial.focus()

    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose?.()
        return
      }

      if (e.key !== 'Tab') return

      const currentFocus = document.activeElement
      const nextFocusables = getFocusableElements(container)
      if (!nextFocusables.length) return

      const first = nextFocusables[0]
      const last = nextFocusables[nextFocusables.length - 1]

      if (e.shiftKey) {
        if (currentFocus === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (currentFocus === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('keydown', handler)
      if (lastActive && typeof lastActive.focus === 'function') lastActive.focus()
    }
  }, [onClose, modalRef])
}

